import {useState, useEffect, forwardRef, Children, isValidElement} from 'react';
import {FormControl, InputGroup} from 'react-bootstrap';
import type {ReactNode} from 'react';

interface CustomMenuProps {
	children: ReactNode;
	style: React.CSSProperties;
	className: string;
	'aria-labelledby': string;
	isOpen: boolean;
}

const CustomMenu = forwardRef<HTMLDivElement, CustomMenuProps>(
	({children, style, className, 'aria-labelledby': labeledBy, isOpen}, ref) => {
		const [value, setValue] = useState('');
		const [visibleItems, setVisibleItems] = useState<ReactNode[]>([]);
		const [page, setPage] = useState(0);
		const itemsPerPage = 5;

		useEffect(() => {
			if (isOpen) {
				setValue('');
				setPage(0);
			}
		}, [isOpen]);

		useEffect(() => {
			const filteredChildren = Children.toArray(children).filter(
				child =>
					isValidElement(child) &&
					(!value ||
						(isValidElement(child) &&
							typeof child.props.children === 'string' &&
							child.props.children
								.toLowerCase()
								.startsWith(value.toLowerCase()))),
			);
			setVisibleItems(filteredChildren.slice(0, (page + 1) * itemsPerPage));
		}, [value, children, page]);

		const handleScroll = (e: React.UIEvent<HTMLUListElement>) => {
			const bottom =
				e.currentTarget.scrollHeight - e.currentTarget.scrollTop ===
				e.currentTarget.clientHeight;
			if (bottom && visibleItems.length < Children.toArray(children).length) {
				setPage(prevPage => prevPage + 1);
			}
		};

		return (
			<div
				ref={ref}
				style={style}
				className={className}
				aria-labelledby={labeledBy}>
				<InputGroup className="mx-3 my-2 w-auto input-with-icon">
					<FormControl
						autoFocus
						placeholder="Type to filter..."
						onChange={e => setValue(e.target.value)}
						value={value}
					/>
					<i
						className="bi-1 bi-search"
						title=""
						data-toggle="tooltip"
						data-original-title="Search"
					/>
				</InputGroup>
				<ul
					className="list-unstyled"
					style={{maxHeight: '140px', overflowY: 'auto'}}
					onScroll={handleScroll}>
					{visibleItems}
				</ul>
			</div>
		);
	},
);

export default CustomMenu;
